import Toast from 'react-native-toast-message';
import {makeAutoObservable, onBecomeObserved, runInAction} from 'mobx';
import {makeLoggable} from 'mobx-log';

import {ERROR_MESSAGE} from '../components/ToastBottom';
import apiService from '../transport/apiService';
import {ApplyOfferSuccessResponse, OffersItemWithMflId, SignUpOfferData} from '../transport/types';
import {logError} from '../utils/errorLogging';
import {logger, serializeError} from '../utils/logger';
import {ApplyOfferError, parseApplyOfferError, shouldAutoRetry} from '../utils/offerErrorHandler';

import type {RootStore} from './rootStore';

/**
 * Retry configuration for offer apply API calls.
 *
 * MAX_RETRIES: Total retry attempts (not counting initial request)
 * RETRY_DELAYS: Delay (ms) before each retry using exponential backoff
 *
 * Worst-case timeline: 3 attempts over 3 seconds
 * - Initial attempt: immediate
 * - Retry 1: after 1000ms
 * - Retry 2: after 2000ms (3000ms total from start)
 */
const MAX_RETRIES = 2;
const RETRY_DELAYS = [1000, 2000];

export class OfferDetailStore {
  private rootStore: RootStore;
  private mflId: string;
  private initialOffer: OffersItemWithMflId;

  linkedProducts: unknown[] | null = null;
  isLoadingLinkedProducts: boolean = false;
  isLoadingCoffeeCardStats: boolean = false;
  isApplyLoading: boolean = false;
  applyError: ApplyOfferError | null = null;

  /**
   * Live computed getter — always reads from offersStore.data so canApply,
   * disabledReason etc. stay in sync after refreshOffers() replaces the array.
   * Falls back to the initial snapshot if offer is removed from the list.
   */
  get offer(): OffersItemWithMflId {
    return this.rootStore.offersStore.data?.find(o => o.mflId === this.mflId) || this.initialOffer;
  }

  constructor(offer: OffersItemWithMflId, rootStore: RootStore) {
    this.mflId = offer.mflId;
    this.initialOffer = offer;
    this.rootStore = rootStore;

    makeAutoObservable(this);
    makeLoggable(this);

    /**
     * Fetch data lazily
     */
    onBecomeObserved(this, 'linkedProducts', this.load);
  }

  private load = async () => {
    await this.loadLinkedProducts();
  };

  private loadLinkedProducts = async () => {
    if (!this.linkedProducts && this.offer?.barcodes?.length > 0) {
      runInAction(() => {
        this.isLoadingLinkedProducts = true;
      });
      apiService
        .searchLines({
          product_codes: this.offer.barcodes.flatMap(value =>
            value.split(';').map(barcode => barcode.trim()),
          ),
        })
        .then((lines: unknown) => {
          runInAction(() => {
            this.linkedProducts = (lines as unknown[]) || [];
            this.isLoadingLinkedProducts = false;
          });
        })
        .catch(e => {
          runInAction(() => {
            this.isLoadingLinkedProducts = false;
          });
          logError(e, 'OfferDetailStore.loadLinkedProducts');
        });
    }
  };

  clearApplyError() {
    this.applyError = null;
  }

  async applyOffer(): Promise<{success: boolean; error?: ApplyOfferError}> {
    runInAction(() => {
      this.isApplyLoading = true;
      this.applyError = null;
    });

    try {
      const zenGlobalId = this.rootStore.rewardsStore.cardNumber || undefined;
      const response = await this.applyWithRetry(this.offer.mflId, zenGlobalId);

      runInAction(() => {
        // Sync activated_deals directly from response — this is the single source of truth
        // for isApplied status across all offer screens (detail, list, compact tile)
        this.rootStore.cartStore.activatedDeals = response.data.activated_deals;
      });

      // Reload order to get fresh adjustments and totals reflecting the applied offer
      await this.rootStore.cartStore.loadOrderForUser();

      return {success: true};
    } catch (error) {
      const parsed = error as ApplyOfferError;

      runInAction(() => {
        this.applyError = parsed;
      });

      // Toast for terminal (non-recoverable) errors only
      if (!parsed.recoverable) {
        Toast.show({
          type: ERROR_MESSAGE,
          text1: parsed.message,
        });
      }

      return {success: false, error: parsed};
    } finally {
      runInAction(() => {
        this.isApplyLoading = false;
      });
    }
  }

  private async applyWithRetry(
    mflId: string,
    zenGlobalId?: string,
    attempt = 0,
  ): Promise<ApplyOfferSuccessResponse> {
    try {
      return await apiService.applyOffer(mflId, zenGlobalId);
    } catch (error) {
      const parsed = parseApplyOfferError(error);

      if (shouldAutoRetry(parsed) && attempt < MAX_RETRIES) {
        await this.sleep(RETRY_DELAYS[attempt]);
        return this.applyWithRetry(mflId, zenGlobalId, attempt + 1);
      }

      throw parsed;
    }
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export class OffersStore {
  private signUpOfferPromise: Promise<SignUpOfferData[]> | undefined;
  private rootStore: RootStore;

  isLoading: boolean = false;
  isLoadingSignUpOffers: boolean = false;

  signUpOfferData: SignUpOfferData[] | null = null;

  data: OffersItemWithMflId[] | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
    makeLoggable(this);

    /**
     * Fetch data lazily - requires authentication
     */
    onBecomeObserved(this, 'data', this.load);

    /**
     * Fetch sign-up offers lazily - public API, no authentication required
     */
    onBecomeObserved(this, 'signUpOfferData', this.loadSignUpOffers);
  }

  private loadSignUpOffers = async () => {
    if (!this.signUpOfferPromise) {
      runInAction(() => {
        this.isLoadingSignUpOffers = true;
      });

      this.signUpOfferPromise = apiService.getSignUpOffers();

      try {
        const signUpOffers = await this.signUpOfferPromise;
        runInAction(() => {
          this.signUpOfferData = signUpOffers;
        });
      } catch (error) {
        logError(error, 'OffersStore.loadSignUpOffers');
        runInAction(() => {
          this.signUpOfferData = [];
        });
      } finally {
        runInAction(() => {
          this.isLoadingSignUpOffers = false;
        });
      }
    }
  };

  private load = async () => {
    runInAction(() => {
      this.isLoading = true;
    });

    const storeNumber = this.rootStore.shopSelectionStore.selectedStore?.store_number;
    const zenCardNumber = this.rootStore.rewardsStore.cardNumber || undefined;

    try {
      const offers = await apiService.getOffersV2(storeNumber, zenCardNumber);
      runInAction(() => {
        this.data = offers;

        // Sync activatedDeals from offers API isActivated field.
        // MFL order response does not include activated_deals, so cartStore
        // loses track of applied deals after page refresh. The offers API
        // isActivated field is the reliable server signal.
        const cartStore = this.rootStore.cartStore;
        const serverActivated = offers.filter(o => o.isActivated).map(o => o.mflId);
        const clientDeals = cartStore.activatedDeals;
        const needsSync =
          clientDeals.length !== serverActivated.length ||
          clientDeals.some(id => !serverActivated.includes(id));
        if (needsSync) {
          cartStore.activatedDeals = serverActivated;
        }
      });
    } catch (err) {
      logger.error('OffersStore', 'Error loading offers from v2 endpoint', serializeError(err));
      runInAction(() => {
        this.data = []; // Graceful degradation - empty list, not broken state
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  /** Re-fetch offers to get fresh canApply/disabledReason after cart changes.
   * Guards against concurrent calls — rapid cart mutations (quantity changes,
   * adding items) can trigger multiple updateOrder completions in quick succession.
   * IMPORTANT: Must return the promise — callers (removeActivatedDeal) await it.
   */
  refreshOffers = () => {
    if (this.isLoading) return Promise.resolve();
    return this.load();
  };

  reset = () => {
    runInAction(() => {
      this.data = null;
    });
  };

  resetSignUpOffers = () => {
    runInAction(() => {
      this.signUpOfferData = null;
      delete this.signUpOfferPromise;
    });
  };
}
